/* Change this file to get your personal Portfolio (TypeScript version) */

// Basic types for the exported shapes (kept permissive where appropriate)
export interface Settings {
  isSplash: boolean;
  useCustomCursor: boolean;
  googleTrackingID?: string;
}

export interface Greeting {
  title?: string;
  title2?: string;
  logo_name?: string;
  nickname?: string;
  full_name?: string;
  subTitle?: string;
  resumeLink?: string;
  mail?: string;
  profile_image_path?: string;
}

export interface SocialMediaLinks {
  github?: string;
  linkedin?: string;
  gmail?: string;
  gitlab?: string;
  twitter?: string;
  instagram?: string;
  x?: string;
}

export interface SoftwareSkillItem {
  skillName: string;
  fontAwesomeClassname?: string;
  style?: Record<string, string>;
  iconifyClass?: string;
}

export interface SkillGroup {
  title: string;
  fileName?: string;
  skills: string[];
  softwareSkills: SoftwareSkillItem[];
}

export interface Skills {
  data: SkillGroup[];
}

export interface Degree {
  title: string;
  subtitle?: string;
  logo_path?: string;
  alt_name?: string;
  duration?: string;
  location?: string;
  descriptions?: string[];
  website_link?: string;
}

export interface Degrees {
  degrees: Degree[];
}

export interface Certification {
  title: string;
  subtitle?: string;
  logo_path?: string;
  iconClass?: string;
  certificate_link?: string;
  alt_name?: string;
  color_code?: string;
}

export interface Certifications {
  certifications: Certification[];
}

export interface ExperienceItem {
  title: string;
  company?: string;
  company_url?: string;
  logo_path?: string;
  duration?: string;
  location?: string;
  description?: string;
  color?: string;
}

export interface ExperienceSection {
  title: string;
  experiences: ExperienceItem[];
}

export interface Experience {
  title?: string;
  subtitle?: string;
  description?: string;
  header_image_path?: string;
  sections?: ExperienceSection[];
}

export interface ProjectLanguage {
  name: string;
  iconifyClass?: string;
}

export interface ProjectLink {
  name: string;
  url: string;
  iconifyClass?: string;
}

export interface Project {
  id: string;
  name: string;
  url?: string;
  description?: string;
  languages?: ProjectLanguage[];
  links?: ProjectLink[];
}

export interface Projects {
  data: Project[];
}

// Website related settings
export const settings: Settings = {
  isSplash: false,
  useCustomCursor: true,
  googleTrackingID: "G-G4ZM8K88SH",
};

// Home Page
export const greeting: Greeting = {
  title: "Hello 👋.",
  title2: "Rexford",
  logo_name: "Rexford",
  nickname: "Rexford",
  full_name: "Rexford Koomson",
  profile_image_path: "hari-crop.jpeg",
  subTitle:
    "Software Engineer building scalable solutions for ecommerce analytics. Team Lead | Cloud Architect | MSc Computer Science.",
  resumeLink: "https://flowcv.com/resume/bec28bp195",
  mail: "mailto:rexford.kk@gmail.com",
};

export const socialMediaLinks: SocialMediaLinks = {
  github: "https://github.com/rexfordkode",
  linkedin: "https://www.linkedin.com/in/rexford-kofi-koomson/",
  x: "https://x.com/originalrexford",
  gmail: "rexford.kk@gmail.com",
};

export const skills: Skills = {
  data: [
    {
      title: "Backend Development",
      fileName: "FullStackImg",
      skills: [
        "Architected microservice ecosystems for DTC analytics products with clear domain boundaries and service ownership",
        "Led backend modernization efforts that reduced query latency by 40% using Node.js, NestJS, and GraphQL Federation",
        "Delivered serverless AI recommendation workflows on AWS Lambda to support real-time decisioning",
        "Designed integration-ready APIs connecting Meta, Google, TikTok Ads, and enterprise CMS platforms",
        "Governed data modeling across PostgreSQL, MongoDB, and DynamoDB for scale, resilience, and maintainability",
      ],
      softwareSkills: [
        {
          skillName: "Node.js",
          fontAwesomeClassname: "devicon-plain:nodejs-wordmark",
          style: { color: "#339933" },
        },
        {
          skillName: "NestJS",
          fontAwesomeClassname: "simple-icons:nestjs",
          style: { color: "#E0234E" },
        },
        {
          skillName: "TypeScript",
          fontAwesomeClassname: "skill-icons:typescript",
          style: { color: "#3178C6" },
        },
        {
          skillName: "Express",
          fontAwesomeClassname: "simple-icons:express",
          style: { color: "#000000" },
        },
        {
          skillName: "GraphQL",
          fontAwesomeClassname: "simple-icons:graphql",
          style: { color: "#E10098" },
        },
        {
          skillName: "Python",
          fontAwesomeClassname: "simple-icons:python",
          style: { color: "#3776AB" },
        },
        {
          skillName: "Flask",
          fontAwesomeClassname: "simple-icons:flask",
          style: { color: "#000000" },
        },
        {
          skillName: "FastAPI",
          fontAwesomeClassname: "simple-icons:fastapi",
          style: { color: "#009688" },
        },
        {
          skillName: "PostgreSQL",
          fontAwesomeClassname: "simple-icons:postgresql",
          style: { color: "#336791" },
        },
        {
          skillName: "MongoDB",
          fontAwesomeClassname: "simple-icons:mongodb",
          style: { color: "#47A248" },
        },
        {
          skillName: "MySQL",
          fontAwesomeClassname: "simple-icons:mysql",
          style: { color: "#4479A1" },
        },
        {
          skillName: "Prisma",
          fontAwesomeClassname: "simple-icons:prisma",
          style: { color: "#2D3748" },
        },
        {
          skillName: "Redis",
          fontAwesomeClassname: "simple-icons:redis",
          style: { color: "#DC382D" },
        },
        {
          skillName: "SQLite",
          fontAwesomeClassname: "simple-icons:sqlite",
          style: { color: "#003B57" },
        },
        {
          skillName: "Drizzle ORM",
          fontAwesomeClassname: "mdi:table",
          style: { color: "#56BE6A" },
        },
        {
          skillName: "Apache Kafka",
          fontAwesomeClassname: "simple-icons:apachekafka",
          style: { color: "#231F20" },
        },
        {
          skillName: "RabbitMQ",
          fontAwesomeClassname: "simple-icons:rabbitmq",
          style: { color: "#FF6600" },
        },
        {
          skillName: "Postman",
          fontAwesomeClassname: "simple-icons:postman",
          style: { color: "#FF6C37" },
        },
        {
          skillName: "Swagger",
          fontAwesomeClassname: "simple-icons:swagger",
          style: { color: "#85EA2D" },
        },
        {
          skillName: "React Native",
          fontAwesomeClassname: "simple-icons:react",
          style: { color: "#61DAFB" },
        },
        {
          skillName: "Gemini API",
          fontAwesomeClassname: "simple-icons:gemini",
          style: { color: "#FF6B6B" },
        },
        {
          skillName: "OpenAI API",
          fontAwesomeClassname: "simple-icons:openai",
          style: { color: "#412991" },
        },
      ],
    },
    {
      title: "Cloud & DevOps",
      fileName: "CloudInfraImg",
      skills: [
        "Owned AWS-first platform architecture (Lambda, DynamoDB, S3) for high-volume, low-latency workloads",
        "Standardized containerization with Docker to reduce release friction and improve environment parity",
        "Implemented CI/CD pipelines with automated quality gates for reliable, zero-downtime deployment paths",
        "Architected event-driven services with Kafka and RabbitMQ for real-time processing and fault tolerance",
        "Managed cloud operations with cost-performance guardrails while scaling for sustained traffic growth",
      ],
      softwareSkills: [
        {
          skillName: "AWS",
          fontAwesomeClassname: "simple-icons:amazonaws",
          style: { color: "#FF9900" },
        },
        {
          skillName: "Docker",
          fontAwesomeClassname: "simple-icons:docker",
          style: { color: "#1488C6" },
        },
        {
          skillName: "Lambda",
          fontAwesomeClassname: "simple-icons:awslambda",
          style: { color: "#FF9900" },
        },
        {
          skillName: "DynamoDB",
          fontAwesomeClassname: "simple-icons:amazondynamodb",
          style: { color: "#4053D6" },
        },
        {
          skillName: "S3",
          fontAwesomeClassname: "simple-icons:amazons3",
          style: { color: "#569A31" },
        },
        {
          skillName: "RDS",
          fontAwesomeClassname: "simple-icons:amazonrds",
          style: { color: "#527FFF" },
        },
        {
          skillName: "Kibana",
          fontAwesomeClassname: "simple-icons:kibana",
          style: { color: "#005571" },
        },
        {
          skillName: "GitHub Actions",
          fontAwesomeClassname: "simple-icons:githubactions",
          style: { color: "#5b77ef" },
        },
        {
          skillName: "Git",
          fontAwesomeClassname: "simple-icons:git",
          style: { color: "#E94E32" },
        },
        {
          skillName: "Jira",
          fontAwesomeClassname: "simple-icons:jira",
          style: { color: "#0052CC" },
        },
        {
          skillName: "Firebase",
          fontAwesomeClassname: "simple-icons:firebase",
          style: { color: "#FFCA28" },
        },
      ],
    },
    {
      title: "Frontend Development",
      fileName: "DesignImg",
      skills: [
        "Built production-grade React interfaces that translate complex platform capabilities into clear user workflows",
        "Partnered with product stakeholders to shape interaction patterns that improve usability and adoption",
        "Implemented robust state and data integration patterns for API-driven applications",
        "Maintained design-system-aware frontend standards across HTML5, CSS3, and modern JavaScript tooling",
      ],
      softwareSkills: [
        {
          skillName: "React",
          fontAwesomeClassname: "simple-icons:react",
          style: { color: "#61DAFB" },
        },
        {
          skillName: "Vue.js",
          fontAwesomeClassname: "simple-icons:vuedotjs",
          style: { color: "#41B883" },
        },
        {
          skillName: "Mendix",
          fontAwesomeClassname: "simple-icons:mendix",
          style: { color: "#0595DB" },
        },
        {
          skillName: "JavaScript",
          fontAwesomeClassname: "simple-icons:javascript",
          style: { backgroundColor: "#FFFFFF", color: "#F7DF1E" },
        },
        {
          skillName: "HTML5",
          fontAwesomeClassname: "simple-icons:html5",
          style: { color: "#E34F26" },
        },
        {
          skillName: "CSS3",
          fontAwesomeClassname: "simple-icons:css3",
          style: { color: "#1572B6" },
        },
      ],
    },
  ],
};

export const degrees: Degrees = {
  degrees: [
    {
      title: "Grand Valley State University",
      subtitle:
        "MSc Applied Computer Science (Major: Software Engineering & AI)",
      logo_path: "efi.png",
      alt_name: "GVSU",
      duration: "Aug 2025 - Present",
      location: "Allendale, Michigan, USA",
      descriptions: [
        "⚡ Currently pursuing Master's degree with focus on Software Engineering and Artificial Intelligence",
        "⚡ Learning advanced topics in machine learning, distributed systems, and software architecture",
        "⚡ Working as Desktop Support Technician at GVSU IT Service while studying",
      ],
      website_link: "https://www.gvsu.edu/",
    },
    {
      title: "Ghana Communication Technology University",
      subtitle: "BSc Information Technology (Major: Software Development)",
      logo_path: "ssec.png",
      alt_name: "GCTU",
      duration: "Sep 2015 - Aug 2019",
      location: "Accra, Ghana",
      descriptions: [
        "⚡ Studied core Computer Science subjects including Data Structures, Algorithms, DBMS, Networking, and Security",
        "⚡ Completed various projects in Backend Development, Web Development, and Mobile App Development",
        "⚡ Built strong foundation in software development principles and practices",
        "⚡ Gained hands-on experience with multiple programming languages and frameworks",
      ],
      website_link: "http://www.gctu.edu.gh/",
    },
  ],
};

export const certifications: Certifications = {
  certifications: [
    {
      title: "Problem Solving & Algorithms",
      subtitle: "HackerRank Intermediate",
      logo_path: "",
      iconClass: "fa-brands fa-hackerrank",
      certificate_link:
        "https://www.hackerrank.com/certificates/iframe/47598ec9132b",
      alt_name: "HackerRank",
      color_code: "#00EA64",
    },
    {
      title: "JavaScript Algorithms & Data Structures",
      subtitle: "freeCodeCamp Certification",
      logo_path: "",
      iconClass: "fa-brands fa-free-code-camp",
      certificate_link:
        "https://freecodecamp.org/certification/fcc4f6ee1b2-3f5a-424c-bf45-26ae92bd8857/javascript-algorithms-and-data-structures",
      alt_name: "freeCodeCamp",
      color_code: "#0A0A23",
    },
    {
      title: "Full Stack Web Development",
      subtitle: "Codecademy",
      logo_path: "",
      iconClass: "fa-solid fa-code",
      certificate_link:
        "https://www.codecademy.com/profiles/rexfordkode/certificates/61f06e05699fbb00175b229a",
      alt_name: "Codecademy",
      color_code: "#1F4788",
    },
    {
      title: "MongoDB Atlas Security",
      subtitle: "MongoDB University",
      logo_path: "mongo.png",
      iconClass: "fa-solid fa-database",
      certificate_link:
        "https://drive.google.com/file/d/12u_tkvhgB-K2TIm-RDdJOwYbQ9ccqiqA/view?usp=sharing",
      alt_name: "MongoDB University",
      color_code: "#00ED64",
    },
    {
      title: "Android Development",
      subtitle: "Google Africa Developer Training",
      logo_path: "google_logo.png",
      iconClass: "fa-brands fa-google",
      certificate_link:
        "https://drive.google.com/file/d/1ws7g2Oepu76vDFir6SvR_emFAMSkB2ZQ/view?usp=sharing",
      alt_name: "Google Africa",
      color_code: "#4285F4",
    },
    {
      title: "Front End Web Developer",
      subtitle: "Udacity Nanodegree",
      logo_path: "",
      iconClass: "fa-solid fa-graduation-cap",
      certificate_link:
        "https://drive.google.com/file/d/1JTd8pVlHbP0RVQYtfa4OZgJYGfdiCwM6/view?usp=sharing",
      alt_name: "Udacity",
      color_code: "#02B3E4",
    },
    {
      title: "Postman Student Expert",
      subtitle: "Postman",
      logo_path: "postman.png",
      iconClass: "fa-solid fa-paper-plane",
      certificate_link:
        "https://badgr.com/public/assertions/h2qevHs4SlyoHErJQ0mn2g",
      alt_name: "Postman",
      color_code: "#FF6C37",
    },
    {
      title: "Mendix Rapid Developer Certification",
      subtitle: "Mendix",
      logo_path: "",
      iconClass: "fa-solid fa-bolt",
      certificate_link:
        "https://academy.mendix.com/link/resultsandcertificates",
      alt_name: "Mendix",
      color_code: "#0595DB",
    },
  ],
};

export const experience: Experience = {
  title: "Experience",
  subtitle: "Building Enterprise Solutions That Scale",
  description:
    "7+ years engineering high-impact solutions for global DTC brands. Led cross-functional teams to architect microservices and analytics platforms processing millions of events daily. Transformed legacy systems into modern cloud-native applications, reducing infrastructure costs by 40% while improving performance. Specialized in Node.js, TypeScript, GraphQL, AWS, and AI integration—bridging technical excellence with business outcomes.",
  header_image_path: "experience.svg",
  sections: [
    {
      title: "Work Experience",
      experiences: [
        {
          title: "Software Developer",
          company: "Grand Valley State University - Blue Nucleus Team",
          company_url:
            "https://www.gvsu.edu/bluenucleus/about-blue-nucleus-5.htm",
          logo_path: "gvsu.png",
          duration: "Feb 2026 - Present",
          location: "Allendale, Michigan, USA",
          description: `Contributing to research and development projects focused on innovative software solutions. Collaborating with interdisciplinary teams to design and implement applications that support academic research initiatives. Gaining experience in cutting-edge technologies while applying software engineering principles to real-world problems.`,
          color: "#0071c5",
        },
        {
          title: "Desktop Support Technician",
          company: "Grand Valley State University - IT Service",
          company_url: "https://www.gvsu.edu/",
          logo_path: "gvsu.png",
          duration: "Aug 2025 - Present",
          location: "Allendale, Michigan, USA",
          description: `Providing technical support for university IT infrastructure and services. Troubleshooting hardware and software issues for faculty, staff, and students. Managing IT service requests and maintaining university technology systems.`,
          color: "#0071c5",
        },
        {
          title: "Software Engineer",
          company: "Heatmap",
          company_url: "https://www.heatmap.com/",
          logo_path: "heatmap.png",
          duration: "Jan 2024 - Oct 2025",
          location: "Remote (USA)",
          description: `Led engineering team building revenue-attribution analytics platform for ecommerce, serving DTC brands including Jones Road, Loop Earplugs, and Obvi. Architected microservices infrastructure with 99.9% uptime using Node.js and TypeScript. Designed serverless AWS architecture (Lambda, DynamoDB, S3) reducing infrastructure costs by 35% while handling high-volume event processing. Built heatmapAI—an ML-powered recommendation engine delivering actionable CRO insights. Implemented GraphQL Federation enabling real-time data aggregation across multiple services. Spearheaded integrations with Meta, Google, TikTok Ads, and A/B testing platforms (Intelligems, Visually).`,
          color: "#ff6b6b",
        },
        {
          title: "Software Engineer",
          company: "Amalitech Services",
          company_url: "https://amalitech.com/",
          logo_path: "amalitech.png",
          duration: "Mar 2022 - Jun 2025",
          location: "Accra, Ghana",
          description: `Spearheaded complete backend transformation of enterprise ERP system, migrating from legacy PHP to modern Node.js/GraphQL stack—improving response times by 40% and developer velocity by 60%. Architected 7+ production microservices using TypeScript, Prisma ORM, and PostgreSQL with clean architecture, serving 10,000+ daily users. Led team of 4 developers through code reviews and technical mentorship, reducing bug reports by 45%. Built critical integrations with Storyblok CMS, Jira, and Smartling Translation API, automating workflows that saved clients 20+ hours/week. Collaborated directly with stakeholders to translate business requirements into scalable technical solutions.`,
          color: "#1e88e5",
        },
        {
          title: "IT Consultant & Owner",
          company: "Jorexnet Solution",
          company_url: "",
          logo_path: "",
          duration: "Oct 2019 - Feb 2023",
          location: "Takoradi, Ghana",
          description: `Founded and managed an IT consultancy providing comprehensive technology solutions. Specialized in network infrastructure design and implementation, security system installations including CCTV surveillance systems, and hardware installations. Developed custom applications for local businesses and provided digital business solutions to help companies establish their online presence. Delivered end-to-end IT services from consultation to deployment and ongoing support.`,
          color: "#9c27b0",
        },
      ],
    },
  ],
};

export const projectsHeader = {
  title: "Projects",
  description:
    "From AI-powered analytics platforms to enterprise incident management systems, I build solutions that scale. Each project below demonstrates real-world impact—whether it's processing large-scale data, automating workflows for enterprise clients, or architecting microservices that power critical business operations.",
  avatar_image_path: "projects_image.svg",
};

export const contactPageData = {
  contactSection: {
    title: "Contact Me",
    profile_image_path: "hari-crop.png",
    description:
      "Let's build something amazing together! Whether you're looking to scale your infrastructure, architect microservices, or integrate AI solutions—I'm always excited to discuss new opportunities and technical challenges.",
  },
  blogSection: {
    title: "Blogs",
    subtitle:
      "I don't blog frequently but when I do something awesome, I do try to document it so it can be helpful to others. I write on Twitter.",
    link: "https://x.com/originalrexford",
    avatar_image_path: "blogs_image.svg",
  },
};

export const projects: Projects = {
  data: [
    {
      id: "0",
      name: "Reach App",
      url: "#",
      description:
        "A social networking application designed specifically for college students. Built with modern technologies to facilitate campus connections and student engagement. Led the team as Team Lead on this project.",
      languages: [
        { name: "Node.js", iconifyClass: "logos-nodejs" },
        { name: "React", iconifyClass: "logos-react" },
        { name: "TypeScript", iconifyClass: "skill-icons:typescript" },
        { name: "PostgreSQL", iconifyClass: "logos-postgresql" },
      ],
      links: [
        {
          name: "Google Play",
          url: "https://play.google.com/store/apps/details?id=com.amalitech.reach&hl=en_US",
          iconifyClass: "logos:google-play-icon",
        },
        {
          name: "App Store",
          url: "https://apps.apple.com/gh/app/reach-by-amalitech/id6692629957",
          iconifyClass: "logos:apple-app-store",
        },
      ],
    },
    {
      id: "1",
      name: "Storyblok CMS Integration",
      url: "#",
      description:
        "Middleware backend service integrating Storyblok CMS with Translation API for multilingual content management. Streamlines content translation workflows for enterprise clients.",
      languages: [
        { name: "Node.js", iconifyClass: "logos-nodejs" },
        { name: "TypeScript", iconifyClass: "skill-icons:typescript" },
        { name: "Storyblok", iconifyClass: "logos-storyblok-icon" },
        { name: "Smartling API", iconifyClass: "mdi:api" },
      ],
      links: [],
    },
    {
      id: "2",
      name: "Incontext Incident Dashboard",
      url: "#",
      description:
        "Enterprise-grade incident reporting API with Jira integration. Enables seamless incident tracking, reporting, and management for large organizations.",
      languages: [
        { name: "Node.js", iconifyClass: "logos-nodejs" },
        { name: "NestJS", iconifyClass: "simple-icons:nestjs" },
        { name: "TypeScript", iconifyClass: "skill-icons:typescript" },
        { name: "Jira API", iconifyClass: "logos-jira" },
        { name: "PostgreSQL", iconifyClass: "logos-postgresql" },
      ],
      links: [],
    },
    {
      id: "3",
      name: "AI Grader Tool",
      url: "#",
      description:
        "AI-powered grading system with comprehensive site analytics. Leverages machine learning to provide automated assessment and detailed performance insights.",
      languages: [
        { name: "Python", iconifyClass: "logos-python" },
        { name: "FastAPI", iconifyClass: "simple-icons:fastapi" },
        { name: "Node.js", iconifyClass: "logos-nodejs" },
        { name: "AI/ML", iconifyClass: "carbon:machine-learning-model" },
        { name: "Docker", iconifyClass: "simple-icons:docker" },
        { name: "Gemini API", iconifyClass: "mdi:gemini" },
        { name: "PostgreSQL", iconifyClass: "logos-postgresql" },
      ],
      links: [],
    },
    {
      id: "4",
      name: "Heatmap AI Insight Engine",
      url: "#",
      description:
        "AI-powered CRO recommendation engine that analyzes trillions of ecommerce data points to identify conversion killers and revenue drivers. Provides specific website optimization suggestions to maximize Revenue Per Session (RPS) for DTC brands.",
      languages: [
        { name: "Node.js", iconifyClass: "logos-nodejs" },
        { name: "Python", iconifyClass: "logos-python" },
        { name: "AWS Lambda", iconifyClass: "simple-icons:awslambda" },
        { name: "AI/ML", iconifyClass: "carbon:machine-learning-model" },
        { name: "DynamoDB", iconifyClass: "simple-icons:amazondynamodb" },
        { name: "Docker", iconifyClass: "simple-icons:docker" },
        { name: "PostgreSQL", iconifyClass: "logos-postgresql" },
        { name: "Gemini API", iconifyClass: "mdi:gemini" },
      ],
      links: [
        {
          name: "Website",
          url: "#",
          iconifyClass: "mdi:web",
        },
      ],
    },
    {
      id: "5",
      name: "Vital Gourd App",
      url: "#",
      description:
        "A focused wellness app for tracking daily nutrition habits, helping users stay consistent with healthy routines and simple meal planning.",
      languages: [
        { name: "React", iconifyClass: "logos-react" },
        { name: "TypeScript", iconifyClass: "skill-icons:typescript" },
        { name: "Node.js", iconifyClass: "logos-nodejs" },
      ],
      links: [
        {
          name: "Website",
          url: "#",
          iconifyClass: "mdi:web",
        },
      ],
    },
    {
      id: "6",
      name: "Nourish App",
      url: "https://www.nourishhub.org/",
      description:
        "A community-focused app for sharing support, coordinating help, and keeping conversations private and simple.",
      languages: [
        { name: "React", iconifyClass: "logos-react" },
        { name: "TypeScript", iconifyClass: "skill-icons:typescript" },
      ],
      links: [
        {
          name: "Website",
          url: "https://www.nourishhub.org/",
          iconifyClass: "mdi:web",
        },
      ],
    },
    {
      id: "7",
      name: "Bloggify",
      url: "#",
      description: "A Simple REST API for Blog Application.",
      languages: [{ name: "NodeJS", iconifyClass: "logos-nodejs" }],
      links: [
        {
          name: "Github",
          url: "#",
          iconifyClass: "ri:github-fill",
        },
      ],
    },
    {
      id: "8",
      name: "Stargazzers",
      url: "#",
      description: "An Unofficial API for GitHub Repo Stars Count",
      languages: [{ name: "NodeJs", iconifyClass: "logos-nodejs" }],
      links: [
        {
          name: "Github",
          url: "#",
          iconifyClass: "ri:github-fill",
        },
      ],
    },
    {
      id: "9",
      name: "GitHub Theme Portfolio",
      url: "#",
      description:
        "A Personal Portfolio Website that showcases my work and experience. which is hosted on Github Pages.",
      languages: [
        { name: "HTML5", iconifyClass: "vscode-icons:file-type-html" },
        { name: "CSS3", iconifyClass: "vscode-icons:file-type-css" },
        { name: "JavaScript", iconifyClass: "logos-javascript" },
        { name: "SCSS", iconifyClass: "vscode-icons:file-type-scss2" },
      ],
      links: [
        {
          name: "Github",
          url: "#",
          iconifyClass: "ri:github-fill",
        },
      ],
    },
    {
      id: "10",
      name: "AI Questions interaction and response loop for decision making",
      url: "#",
      description:
        "An AI-driven system that allows users to ask questions, receives responses, and uses that interaction to inform subsequent decisions, creating a feedback loop for continuous learning and improvement.",
      languages: [
        { name: "React Native", iconifyClass: "logos-react-native" },
        { name: "AI/ML", iconifyClass: "carbon:machine-learning-model" },
        { name: "Node.js", iconifyClass: "logos-nodejs" },
        { name: "Firebase", iconifyClass: "simple-icons:firebase" },
        { name: "React", iconifyClass: "logos-react" },
      ],
      links: [
        {
          name: "Github",
          url: "#",
          iconifyClass: "ri:github-fill",
        },
      ],
    },
  ],
};
